(() => {
  'use strict';
  function init(root) {
    if (root.dataset.initialised) return;
    root.dataset.initialised = 'true';
    const looks = [...root.querySelectorAll('[data-look]')];
    const tabs = [...root.querySelectorAll('[data-look-target]')];
    const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    let current = 0, busy = false;
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: root.dataset.currency || 'USD' });
    function show(index) {
      if (!looks.length || index < 0) return;
      current = (index + looks.length) % looks.length;
      looks.forEach((look, i) => { look.hidden = i !== current; });
      tabs.forEach(tab => tab.setAttribute('aria-pressed', String(tab.dataset.lookTarget === looks[current].dataset.look)));
    }
    tabs.forEach(tab => tab.addEventListener('click', () => show(looks.findIndex(look => look.dataset.look === tab.dataset.lookTarget))));
    root.addEventListener('shopify:block:select', event => {
      const index = looks.findIndex(look => look.dataset.look === event.detail.blockId);
      if (index >= 0) show(index);
    });
    function updateCount(count) {
      document.querySelectorAll('[data-count],[data-cosmic-count]').forEach(node => { node.textContent = count; });
    }
    async function cartCount() {
      try {
        const path = (root.dataset.cart || '/cart') + '.js';
        const response = await fetch(path, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        updateCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (_) { /* A count refresh failure must not retry an already successful add. */ }
    }
    async function add(look, variants) {
      if (busy) return;
      const status = look.querySelector('[data-status]');
      if (!variants.length || variants.some(variant => !variant || !variant.available)) {
        status.textContent = 'Select an available option for each piece.';
        return;
      }
      busy = true;
      root.setAttribute('aria-busy', 'true');
      looks.forEach(item => item._refresh());
      status.textContent = 'Adding to bag…';
      try {
        const path = (root.dataset.root || '/').replace(/\/?$/, '/') + 'cart/add.js';
        const response = await fetch(path, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: variants.map(variant => ({ id: variant.id, quantity: 1 })) })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not add these pieces.');
        status.textContent = 'Added to bag.';
        await cartCount();
      } catch (error) {
        status.textContent = error.message || 'Unable to add. Check your bag before retrying.';
      } finally {
        busy = false;
        root.removeAttribute('aria-busy');
        looks.forEach(item => item._refresh());
      }
    }
    looks.forEach(look => {
      const pieces = [...look.querySelectorAll('[data-piece]')].map(card => {
        let variants = [], parseError = false;
        try { variants = JSON.parse(card.querySelector('[data-variants]').textContent); }
        catch (_) { parseError = true; }
        const groups = [...card.querySelectorAll('[data-option-group]')];
        const buttons = [...card.querySelectorAll('button[data-option]')];
        const selections = new Map();
        const addButton = card.querySelector('[data-add-piece]');
        function chosen() {
          if (!groups.length) return variants.find(v => String(v.id) === card.querySelector('[data-default-variant]')?.value) || null;
          if (groups.some(group => !selections.has(Number(group.dataset.optionGroup)))) return null;
          return variants.find(v => [...selections].every(([index, value]) => v.options[index] === value)) || null;
        }
        function refresh() {
          const variant = chosen();
          const selected = !groups.length || groups.every(group => selections.has(Number(group.dataset.optionGroup)));
          card.querySelector('[data-availability]').textContent = parseError ? 'PRODUCT OPTIONS COULD NOT LOAD' : variant ? (variant.available ? '' : 'SOLD OUT') : selected ? 'UNAVAILABLE COMBINATION' : 'CHOOSE YOUR OPTIONS';
          addButton.disabled = busy || !variant || !variant.available;
          buttons.forEach(button => {
            const index = Number(button.dataset.option), value = button.dataset.value;
            const available = variants.some(v => v.available && v.options[index] === value && [...selections].every(([other, selection]) => other === index || v.options[other] === selection));
            button.setAttribute('aria-pressed', String(selections.get(index) === value));
            button.classList.toggle('is-unavailable', !available);
            button.title = available ? value : value + ' — unavailable with current selections';
            // Keep unavailable combinations selectable so shoppers can change either option first.
            button.disabled = busy;
          });
          if (variant) card.querySelector('[data-price]').textContent = variant.money;
          const url = new URL(card.dataset.url, location.origin);
          if (variant) url.searchParams.set('variant', variant.id);
          card.querySelector('[data-piece-link]').href = url.toString();
        }
        buttons.forEach(button => button.addEventListener('click', () => {
          selections.set(Number(button.dataset.option), button.dataset.value);
          look.querySelector('[data-status]').textContent = '';
          look._refresh();
        }));
        addButton.addEventListener('click', () => add(look, [chosen()]));
        return { card, chosen, refresh, groups, selections };
      });
      look._refresh = () => {
        pieces.forEach(piece => piece.refresh());
        const selected = pieces.map(piece => piece.chosen());
        const both = look.querySelector('[data-add-both]');
        const total = look.querySelector('[data-total]');
        if (both) both.disabled = busy || selected.length !== 2 || selected.some(v => !v || !v.available);
        if (total) total.textContent = selected.length === 2 && selected.every(Boolean) ? 'BOTH PIECES / ' + formatter.format(selected.reduce((sum, v) => sum + v.price, 0) / 100) : 'SELECT OPTIONS FOR BOTH PIECES';
      };
      look.querySelector('[data-add-both]')?.addEventListener('click', () => add(look, pieces.map(piece => piece.chosen())));
      look.querySelector('[data-prev]')?.addEventListener('click', () => show((current - 1 + looks.length) % looks.length));
      look.querySelector('[data-next]')?.addEventListener('click', () => show(current + 1));
      look.querySelector('[data-reveal]')?.addEventListener('click', () => {
        const panel = look.querySelector('.cs2-panel');
        const missing = pieces.flatMap(piece => piece.groups.filter(group => !piece.selections.has(Number(group.dataset.optionGroup))).map(group => piece.card.querySelector('[data-option="' + group.dataset.optionGroup + '"]')));
        look.querySelector('[data-status]').textContent = missing.length ? 'Choose ' + missing.length + ' remaining option' + (missing.length === 1 ? '' : 's') + ' to shop this look.' : 'Select Add piece or Add both to bag.';
        panel.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
        (missing[0] || panel).focus({ preventScroll: true });
      });
      look.querySelectorAll('[data-image]').forEach(button => button.addEventListener('click', () => {
        const photo = look.querySelector('.cs2-photo'), image = photo.querySelector('.cs2-main');
        if (!image) return;
        image.removeAttribute('srcset');
        image.src = button.dataset.image;
        image.alt = button.getAttribute('aria-label') || 'Campaign photograph';
        look.querySelectorAll('[data-image]').forEach(other => other.setAttribute('aria-pressed', String(other === button)));
        photo.classList.toggle('cs2-detail', !button.hasAttribute('data-full-look'));
        look.querySelectorAll('[data-hotspot]').forEach(spot => spot.classList.remove('is-open'));
      }));
      look.querySelectorAll('[data-hotspot]').forEach(spot => {
        const card = look.querySelector('[data-piece-number="' + spot.dataset.hotspot + '"]');
        const highlight = enabled => card?.classList.toggle('is-highlighted', enabled);
        spot.addEventListener('pointerenter', () => highlight(true));
        spot.addEventListener('pointerleave', () => highlight(false));
        spot.addEventListener('focus', () => highlight(true));
        spot.addEventListener('blur', () => { spot.classList.remove('is-open'); highlight(false); });
        spot.addEventListener('click', event => {
          if (matchMedia('(hover: none)').matches && !spot.classList.contains('is-open')) {
            event.preventDefault();
            look.querySelectorAll('[data-hotspot]').forEach(other => other.classList.remove('is-open'));
            spot.classList.add('is-open');
          }
        });
        spot.addEventListener('keydown', event => { if (event.key === 'Escape') spot.classList.remove('is-open'); });
      });
      look._refresh();
    });
    show(0);
  }
  function scan(scope) {
    if (scope.matches?.('[data-cs2]')) init(scope);
    scope.querySelectorAll('[data-cs2]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();